import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { StatCard } from '../components/StatCard';
import { formatDate } from '../utils/format';
import { DEMO_DOC_EXPIRATIONS, LEGAJO_CATALOG_TYPES } from '../data/commercialDemo';

const LEGAJO_TABS = /** @type {const} */ (['documentos', 'recibos', 'vencimientos', 'compliance', 'storage']);

const DOC_TYPE_OPTIONS = [
  { value: 'identification', labelKey: 'legajo.docType_identification' },
  { value: 'contract', labelKey: 'legajo.docType_contract' },
  { value: 'labor_registration', labelKey: 'legajo.docType_labor_registration' },
  { value: 'bank_account', labelKey: 'legajo.docType_bank_account' },
  { value: 'medical_certificate', labelKey: 'legajo.docType_medical_certificate' },
  { value: 'cv', labelKey: 'legajo.docType_cv' },
  { value: 'policy', labelKey: 'legajo.docType_policy' },
  { value: 'payslip', labelKey: 'legajo.docType_payslip' },
  { value: 'training_certificate', labelKey: 'legajo.docType_training_certificate' },
  { value: 'performance_review', labelKey: 'legajo.docType_performance_review' },
  { value: 'other', labelKey: 'legajo.docType_other' },
];

const EMPTY_FORM = {
  employee_id: '',
  document_type: '',
  title: '',
  description: '',
  required: false,
  status: 'pending',
  expires_at: '',
  file_url: '',
  file_name: '',
  storage_provider: 'pending',
};

const STATUSES = ['pending', 'uploaded', 'approved', 'rejected', 'expired'];
const STORAGE = ['pending', 's3', 'digitalocean_spaces', 'external_url', 'metadata_only'];

function badgeVariant(status) {
  const m = {
    pending: 'hr_pending',
    uploaded: 'hr_uploaded',
    approved: 'hr_approved',
    rejected: 'hr_rejected',
    expired: 'hr_expired',
  };
  return m[status] || 'hr_pending';
}

function receiptBadgeVariant(status) {
  if (status === 'signed') return 'approved';
  if (status === 'available') return 'active';
  if (status === 'observed') return 'pending';
  if (status === 'archived') return 'mutedFallback';
  return 'draft';
}

function docHasBinary(doc) {
  return Boolean(
    doc.storage_key ||
      (doc.file_url && ['external_url', 'digitalocean_spaces', 's3'].includes(doc.storage_provider)),
  );
}

function storageFileStateLabel(doc, t) {
  const st = doc.storage_status || 'pending';
  if (st === 'metadata_only') return t('legajo.fileState.metadata_only');
  if (st === 'uploaded' || doc.storage_key) return t('legajo.fileState.uploaded');
  if (st === 'external' || (doc.file_url && doc.storage_provider === 'external_url')) return t('legajo.fileState.external');
  if (st === 'error') return t('legajo.fileState.error');
  if (doc.storage_provider === 'digitalocean_spaces' && !doc.storage_key) return t('legajo.fileState.spaces_ready');
  return t('legajo.fileState.pending');
}

export function DigitalLegajo() {
  const { t } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [storageConfig, setStorageConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [metaDoc, setMetaDoc] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [payslipModal, setPayslipModal] = useState(null);
  const [tab, setTab] = useState(/** @type {typeof LEGAJO_TABS[number]} */ ('documentos'));

  const filterEmployee = searchParams.get('employee_id') || '';
  const filterStatus = searchParams.get('status') || '';
  const filterType = searchParams.get('document_type') || '';
  const filterRequired = searchParams.get('required') || '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [emRes, docRes, psRes, stRes] = await Promise.all([
        api.get('/api/employees'),
        api.get('/api/hr-documents'),
        api.get('/api/payslips'),
        api.get('/api/storage/config'),
      ]);
      setEmployees(Array.isArray(emRes.data) ? emRes.data : []);
      setDocuments(Array.isArray(docRes.data) ? docRes.data : []);
      setPayslips(Array.isArray(psRes.data) ? psRes.data : []);
      setStorageConfig(stRes.data && typeof stRes.data === 'object' ? stRes.data : null);
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
      setDocuments([]);
      setPayslips([]);
      setStorageConfig(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (filterEmployee && d.employee_id !== filterEmployee) return false;
      if (filterStatus && d.status !== filterStatus) return false;
      if (filterType && d.document_type !== filterType) return false;
      if (filterRequired === 'yes' && !d.required) return false;
      if (filterRequired === 'no' && d.required) return false;
      return true;
    });
  }, [documents, filterEmployee, filterStatus, filterType, filterRequired]);

  const kpis = useMemo(() => {
    const list = documents;
    const total = list.length;
    const pending = list.filter((d) => d.status === 'pending').length;
    const expired = list.filter((d) => d.status === 'expired').length;
    const approved = list.filter((d) => d.status === 'approved').length;
    const compliance = total === 0 ? 100 : Math.round((100 * approved) / total);
    const pays = payslips;
    const receiptsAvailable = pays.filter((p) => p.status === 'available' || p.status === 'signed').length;
    const missingBinary = list.filter((d) => {
      if (!d.required || ['rejected', 'expired'].includes(d.status)) return false;
      return !docHasBinary(d);
    }).length;
    return { total, pending, expired, approved, compliance, receiptsAvailable, missingBinary };
  }, [documents, payslips]);

  const receiptKpis = useMemo(() => {
    const pays = payslips;
    return {
      available: pays.filter((p) => p.status === 'available').length,
      draft: pays.filter((p) => p.status === 'draft').length,
      signed: pays.filter((p) => p.status === 'signed').length,
      observed: pays.filter((p) => p.status === 'observed').length,
      archived: pays.filter((p) => p.status === 'archived').length,
      last: pays.length
        ? pays.reduce(
            (best, p) => {
              const k = p.period_year * 100 + p.period_month;
              return k > best.k ? { k, label: `${p.period_year}-${String(p.period_month).padStart(2, '0')}` } : best;
            },
            { k: 0, label: '—' },
          ).label
        : '—',
    };
  }, [payslips]);

  const mergedExpirations = useMemo(() => {
    const fromApi = documents
      .filter((d) => d.expires_at)
      .map((d) => ({
        id: d.id,
        docKey: null,
        label: d.title || d.document_type,
        employee: d.employee_name || '—',
        due: d.expires_at.slice(0, 10),
        urgency: d.status === 'expired' ? 'high' : 'medium',
        nextKey: 'legajo.nextUpdateId',
      }));
    const demo = DEMO_DOC_EXPIRATIONS.map((x) => ({
      id: x.id,
      docKey: x.docKey,
      label: t(`legajo.catalog_${x.docKey}`),
      employee: x.employee,
      due: x.due,
      urgency: x.urgency,
      nextKey: x.nextKey,
    }));
    return [...demo, ...fromApi].sort((a, b) => new Date(a.due) - new Date(b.due));
  }, [documents, t]);

  const documentTypes = useMemo(() => {
    const s = new Set(documents.map((d) => d.document_type).filter(Boolean));
    return [...s].sort();
  }, [documents]);

  function typeLabel(value) {
    const row = DOC_TYPE_OPTIONS.find((o) => o.value === value);
    if (row) return t(row.labelKey);
    return value;
  }

  async function putDoc(id, body) {
    try {
      await api.put(`/api/hr-documents/${id}`, body);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function postApproveDoc(id) {
    try {
      await api.post(`/api/hr-documents/${id}/approve`);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function postMarkExpired(id) {
    try {
      await api.post(`/api/hr-documents/${id}/mark-expired`);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function fetchDownloadUrl(kind, id) {
    try {
      const path = kind === 'payslip' ? `/api/payslips/${id}/download-url` : `/api/hr-documents/${id}/download-url`;
      const { data } = await api.get(path);
      if (data?.available && data.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        setError(data?.message || t('legajo.downloadUnavailable'));
      }
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function uploadHrFile(docId, file) {
    if (!file) return;
    setUploadBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/hr-documents/${docId}/upload`, fd, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      });
      setUploadTarget(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setUploadBusy(false);
    }
  }

  async function uploadPayslipFile(psId, file) {
    if (!file) return;
    setUploadBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/payslips/${psId}/upload`, fd, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      });
      setUploadTarget(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || t('common.error'));
    } finally {
      setUploadBusy(false);
    }
  }

  function openCreate() {
    setForm({
      ...EMPTY_FORM,
      employee_id: filterEmployee || '',
    });
    setModal('create');
  }

  function openEdit(doc) {
    setForm({
      employee_id: doc.employee_id,
      document_type: doc.document_type,
      title: doc.title,
      description: doc.description || '',
      required: !!doc.required,
      status: doc.status,
      expires_at: doc.expires_at ? doc.expires_at.slice(0, 10) : '',
      file_url: doc.file_url || '',
      file_name: doc.file_name || '',
      storage_provider: doc.storage_provider || 'pending',
    });
    setModal({ mode: 'edit', id: doc.id });
  }

  async function saveCreate() {
    const body = {
      employee_id: form.employee_id,
      document_type: form.document_type,
      title: form.title,
      description: form.description,
      required: form.required,
      status: form.status,
      storage_provider: form.storage_provider,
      file_url: form.file_url || null,
      file_name: form.file_name || null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    try {
      await api.post('/api/hr-documents', body);
      setModal(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function saveEdit() {
    const id = modal.id;
    const body = {
      document_type: form.document_type,
      title: form.title,
      description: form.description,
      required: form.required,
      status: form.status,
      storage_provider: form.storage_provider,
      file_url: form.file_url || null,
      file_name: form.file_name || null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    try {
      await api.put(`/api/hr-documents/${id}`, body);
      setModal(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  async function savePayslipCreate() {
    if (!payslipModal) return;
    const body = {
      employee_id: payslipModal.employee_id,
      period_month: Number(payslipModal.period_month),
      period_year: Number(payslipModal.period_year),
      title: payslipModal.title,
      status: payslipModal.status || 'draft',
      gross_amount_cents: Math.round(Number(payslipModal.gross || 0) * 100),
      net_amount_cents: Math.round(Number(payslipModal.net || 0) * 100),
      currency: payslipModal.currency || 'ARS',
    };
    try {
      await api.post('/api/payslips', body);
      setPayslipModal(null);
      await load();
    } catch (e) {
      console.error(e);
      setError(t('common.error'));
    }
  }

  function rejectDoc(doc) {
    const reason = window.prompt(t('legajo.rejectPrompt'));
    if (reason === null) return;
    putDoc(doc.id, { status: 'rejected', rejection_reason: reason || t('legajo.rejectDefault') });
  }

  const storageHint =
    storageConfig && !storageConfig.enabled ? t('legajo.uploadHintMetadataOnly') : t('legajo.uploadHintReady');

  return (
    <div className="page-stack digital-legajo digital-legajo--v20">
      <header className="legajo-suite-hero panel panel--elevated">
        <div>
          <p className="legajo-suite-hero__eyebrow">{t('legajo.suiteEyebrow')}</p>
          <h1 className="legajo-suite-hero__title">{t('legajo.heroTitle')}</h1>
          <p className="legajo-suite-hero__lead">{t('legajo.heroSubtitle')}</p>
        </div>
      </header>

      <div className="banner banner--info legajo-storage-callout">{t('legajo.metadataHint')}</div>
      <div className="banner banner--muted legajo-secure-storage-banner">{t('legajo.secureStorageBanner')}</div>

      <div className="people-suite-kpis legajo-suite-kpis">
        <StatCard icon="▣" label={t('legajo.kpiTotal')} value={kpis.total} />
        <StatCard icon="◷" label={t('legajo.kpiPending')} value={kpis.pending} tone="warning" />
        <StatCard icon="✓" label={t('legajo.kpiApproved')} value={kpis.approved} tone="success" />
        <StatCard icon="!" label={t('legajo.kpiExpired')} value={kpis.expired} tone="accent" />
        <StatCard icon="◇" label={t('legajo.kpiMissingFile')} value={kpis.missingBinary} tone="accent" />
        <StatCard icon="▤" label={t('legajo.kpiReceipts')} value={kpis.receiptsAvailable} />
        <StatCard icon="◎" label={t('legajo.kpiCompliance')} value={`${kpis.compliance}%`} tone="success" />
      </div>

      {error && <div className="banner banner--info">{error}</div>}

      <div className="settings-v16-tabs legajo-suite-tabs" role="tablist" aria-label={t('legajo.tabsAria')}>
        {LEGAJO_TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`settings-v16-tab${tab === id ? ' is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {t(`legajo.tab_${id}`)}
          </button>
        ))}
      </div>

      {tab === 'documentos' && (
        <>
          <section className="panel panel--elevated legajo-catalog">
            <h2 className="panel__title">{t('legajo.catalogTitle')}</h2>
            <p className="panel__muted">{t('legajo.catalogSubtitle')}</p>
            <ul className="legajo-catalog-grid">
              {LEGAJO_CATALOG_TYPES.map((ty) => (
                <li key={ty} className="legajo-catalog-card">
                  <span className="legajo-catalog-card__label">{t(`legajo.catalog_${ty}`)}</span>
                  <Badge variant="product_prepared">{t('legajo.receiptFileMeta')}</Badge>
                </li>
              ))}
            </ul>
          </section>

          <div className="toolbar toolbar--card legajo-toolbar">
            <div className="legajo-filters">
              <select
                className="input input--select"
                value={filterEmployee}
                onChange={(e) => setFilter('employee_id', e.target.value)}
                aria-label={t('legajo.filterEmployee')}
              >
                <option value="">{t('legajo.allEmployees')}</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}
                  </option>
                ))}
              </select>
              <select
                className="input input--select"
                value={filterStatus}
                onChange={(e) => setFilter('status', e.target.value)}
                aria-label={t('legajo.filterStatus')}
              >
                <option value="">{t('legajo.allStatuses')}</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`legajo.status.${s}`)}
                  </option>
                ))}
              </select>
              <select
                className="input input--select"
                value={filterType}
                onChange={(e) => setFilter('document_type', e.target.value)}
                aria-label={t('legajo.filterType')}
              >
                <option value="">{t('legajo.allTypes')}</option>
                {documentTypes.map((ty) => (
                  <option key={ty} value={ty}>
                    {typeLabel(ty)}
                  </option>
                ))}
              </select>
              <select
                className="input input--select"
                value={filterRequired}
                onChange={(e) => setFilter('required', e.target.value)}
                aria-label={t('legajo.filterRequired')}
              >
                <option value="">{t('legajo.allRequired')}</option>
                <option value="yes">{t('legajo.requiredYes')}</option>
                <option value="no">{t('legajo.requiredNo')}</option>
              </select>
            </div>
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              {t('legajo.newDocument')}
            </button>
          </div>

          <div className="table-wrap table-wrap--rounded">
            <table className="data-table data-table--legajo">
              <thead>
                <tr>
                  <th>{t('legajo.colEmployee')}</th>
                  <th>{t('legajo.colType')}</th>
                  <th>{t('legajo.colTitle')}</th>
                  <th>{t('legajo.colStatus')}</th>
                  <th>{t('legajo.colRequired')}</th>
                  <th>{t('legajo.colExpires')}</th>
                  <th className="hide-sm">{t('legajo.colFileState')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="empty-inline">
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty-inline empty-state--boxed">
                      <span className="empty-state__icon" aria-hidden>
                        ▣
                      </span>
                      <p className="empty-state__title">{t('legajo.emptyTitle')}</p>
                      <p className="empty-state__desc">{t('legajo.emptyDesc')}</p>
                    </td>
                  </tr>
                ) : (
                  filtered.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <strong>{doc.employee_name}</strong>
                      </td>
                      <td>
                        <span className="text-sm">{typeLabel(doc.document_type)}</span>
                      </td>
                      <td>{doc.title}</td>
                      <td>
                        <Badge variant={badgeVariant(doc.status)}>{t(`legajo.status.${doc.status}`)}</Badge>
                      </td>
                      <td>{doc.required ? t('legajo.yes') : t('legajo.no')}</td>
                      <td>{formatDate(doc.expires_at)}</td>
                      <td className="hide-sm">
                        <span className="text-sm text-muted">{storageFileStateLabel(doc, t)}</span>
                      </td>
                      <td>
                        <div className="row-actions legajo-actions">
                          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setMetaDoc(doc)}>
                            {t('legajo.actionMeta')}
                          </button>
                          <button type="button" className="btn btn--sm btn--secondary" onClick={() => setUploadTarget({ kind: 'document', id: doc.id })}>
                            {t('legajo.actionAttach')}
                          </button>
                          <button type="button" className="btn btn--sm btn--secondary" onClick={() => openEdit(doc)}>
                            {t('legajo.actionEdit')}
                          </button>
                          <button type="button" className="btn btn--sm btn--primary" onClick={() => postApproveDoc(doc.id)}>
                            {t('legajo.actionApprove')}
                          </button>
                          <button type="button" className="btn btn--sm btn--danger" onClick={() => rejectDoc(doc)}>
                            {t('legajo.actionReject')}
                          </button>
                          <button type="button" className="btn btn--sm btn--ghost" onClick={() => putDoc(doc.id, { status: 'pending' })}>
                            {t('legajo.actionPending')}
                          </button>
                          <button type="button" className="btn btn--sm btn--ghost" onClick={() => postMarkExpired(doc.id)}>
                            {t('legajo.actionExpired')}
                          </button>
                          <button
                            type="button"
                            className="btn btn--sm btn--ghost"
                            onClick={() => fetchDownloadUrl('document', doc.id)}
                            disabled={!docHasBinary(doc)}
                          >
                            {t('legajo.actionDownload')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'recibos' && (
        <>
          <p className="panel__muted legajo-receipts-banner">{t('legajo.receiptsStorageNote')}</p>
          <div className="people-suite-kpis legajo-receipt-kpis">
            <StatCard icon="▤" label={t('legajo.receiptKpiAvailable')} value={receiptKpis.available} tone="success" />
            <StatCard icon="◷" label={t('legajo.receiptKpiDraft')} value={receiptKpis.draft} tone="warning" />
            <StatCard icon="✓" label={t('legajo.receiptKpiSigned')} value={receiptKpis.signed} />
            <StatCard icon="!" label={t('legajo.receiptKpiObserved')} value={receiptKpis.observed} tone="accent" />
            <StatCard icon="◇" label={t('legajo.receiptKpiLastPeriod')} value={receiptKpis.last} />
          </div>
          <div className="toolbar toolbar--card legajo-toolbar">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() =>
                setPayslipModal({
                  employee_id: filterEmployee || (employees[0] && employees[0].id) || '',
                  period_month: String(new Date().getMonth() + 1),
                  period_year: String(new Date().getFullYear()),
                  title: '',
                  status: 'draft',
                  gross: '',
                  net: '',
                  currency: 'ARS',
                })
              }
            >
              {t('legajo.newPayslip')}
            </button>
          </div>
          <div className="table-wrap table-wrap--rounded">
            <table className="data-table data-table--comfortable">
              <thead>
                <tr>
                  <th>{t('legajo.receiptColPeriod')}</th>
                  <th>{t('legajo.receiptColEmployee')}</th>
                  <th className="hide-sm">{t('legajo.receiptColGross')}</th>
                  <th className="hide-sm">{t('legajo.receiptColNet')}</th>
                  <th>{t('legajo.receiptColStatus')}</th>
                  <th>{t('legajo.receiptColIssued')}</th>
                  <th>{t('legajo.receiptColFile')}</th>
                  <th>{t('legajo.receiptColActions')}</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.period_year}-{String(p.period_month).padStart(2, '0')}
                    </td>
                    <td>
                      <strong>{p.employee_name}</strong>
                    </td>
                    <td className="hide-sm">{(p.gross_amount_cents / 100).toLocaleString('es-AR')}</td>
                    <td className="hide-sm">{(p.net_amount_cents / 100).toLocaleString('es-AR')}</td>
                    <td>
                      <Badge variant={receiptBadgeVariant(p.status)}>{t(`legajo.receiptStatus_${p.status}`)}</Badge>
                    </td>
                    <td>{formatDate(p.issued_at)}</td>
                    <td>
                      <span className="text-sm text-muted">
                        {p.storage_key ? t('legajo.fileState.uploaded') : t('legajo.receiptFileMeta')}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn--sm btn--ghost" onClick={() => setMetaDoc(p)}>
                          {t('legajo.receiptActionMeta')}
                        </button>
                        <button type="button" className="btn btn--sm btn--secondary" onClick={() => setUploadTarget({ kind: 'payslip', id: p.id })}>
                          {t('legajo.receiptActionAttach')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--primary"
                          onClick={async () => {
                            try {
                              await api.post(`/api/payslips/${p.id}/mark-available`);
                              await load();
                            } catch (e) {
                              console.error(e);
                              setError(t('common.error'));
                            }
                          }}
                        >
                          {t('legajo.receiptActionAvailable')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          onClick={async () => {
                            try {
                              await api.post(`/api/payslips/${p.id}/mark-signed`);
                              await load();
                            } catch (e) {
                              console.error(e);
                              setError(t('common.error'));
                            }
                          }}
                        >
                          {t('legajo.receiptActionSign')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          onClick={() => fetchDownloadUrl('payslip', p.id)}
                          disabled={!p.storage_key}
                        >
                          {t('legajo.actionDownload')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'vencimientos' && (
        <section className="panel panel--elevated legajo-expiring">
          <h2 className="panel__title">{t('legajo.expiringTitle')}</h2>
          <ul className="legajo-expiring-list">
            {mergedExpirations.map((row) => (
              <li key={row.id} className="legajo-expiring-card">
                <div>
                  <div className="legajo-expiring-card__doc">{row.label}</div>
                  <div className="text-muted text-sm">
                    {row.employee} · {t('legajo.expColDue')}: {formatDate(row.due)}
                  </div>
                  <p className="legajo-expiring-card__next">
                    <strong>{t('legajo.expColNext')}:</strong> {t(row.nextKey)}
                  </p>
                </div>
                <Badge variant={row.urgency === 'high' ? 'error' : row.urgency === 'medium' ? 'pending' : 'mutedFallback'}>
                  {t(`legajo.urgency_${row.urgency}`)}
                </Badge>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'compliance' && (
        <div className="legajo-compliance-tab">
          <div className="people-suite-kpis legajo-compliance-kpis">
            <StatCard icon="◎" label={t('legajo.complianceGlobalPct')} value={`${kpis.compliance}%`} tone="success" />
            <StatCard icon="◇" label={t('legajo.complianceMissingBinary')} value={kpis.missingBinary} tone="accent" />
            <StatCard icon="◷" label={t('legajo.compliancePendingEmployees')} value={kpis.pending} tone="warning" />
            <StatCard icon="!" label={t('legajo.complianceCriticalMissing')} value={kpis.expired} tone="accent" />
          </div>
          <section className="panel panel--elevated legajo-compliance-block">
            <h3 className="panel__title">{t('legajo.complianceBlockTitle')}</h3>
            <p className="panel__muted">{t('legajo.complianceTabIntro')}</p>
            <p className="panel__muted">{t('legajo.complianceBlockBody')}</p>
            <p className="panel__muted text-sm">{t('legajo.metadataHint')}</p>
          </section>
        </div>
      )}

      {tab === 'storage' && storageConfig && (
        <section className="panel panel--elevated legajo-storage-panel">
          <h2 className="panel__title">{t('legajo.storageTabTitle')}</h2>
          <p className="panel__muted">{t('legajo.storageTabIntro')}</p>
          <ul className="legajo-storage-list">
            <li>
              <strong>{t('legajo.storageFieldMode')}:</strong> {t(`legajo.storageMode_${storageConfig.mode}`)}
            </li>
            <li>
              <strong>{t('legajo.storageFieldStatus')}:</strong> {t(`legajo.storageRuntime_${storageConfig.status}`)}
            </li>
            <li>
              <strong>{t('legajo.storageFieldProvider')}:</strong> {t(`legajo.storageProvider_${storageConfig.provider}`)}
            </li>
            <li>
              <strong>{t('legajo.storageFieldMax')}:</strong> {storageConfig.max_upload_mb} MB
            </li>
            <li>
              <strong>{t('legajo.storageFieldMimes')}:</strong> {(storageConfig.allowed_mime_types || []).join(', ')}
            </li>
            <li>
              <strong>{t('legajo.storageFieldBucket')}:</strong>{' '}
              {storageConfig.bucket_configured ? t('legajo.yes') : t('legajo.no')}
            </li>
            <li>
              <strong>{t('legajo.storageFieldCreds')}:</strong>{' '}
              {storageConfig.credentials_configured ? t('legajo.yes') : t('legajo.no')}
            </li>
          </ul>
          <p className="panel__muted text-sm">{t('legajo.storageFutureNote')}</p>
        </section>
      )}

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{modal === 'create' ? t('legajo.newDocument') : t('legajo.editDocument')}</h2>
            <p className="panel__muted legajo-modal-note">{t('legajo.modalNoteV23')}</p>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('legajo.fieldEmployee')}</span>
                <select
                  className="input"
                  value={form.employee_id}
                  disabled={modal?.mode === 'edit'}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                >
                  <option value="">—</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.first_name} {e.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldType')}</span>
                <select className="input" value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })}>
                  <option value="">—</option>
                  {DOC_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldTitle')}</span>
                <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldDescription')}</span>
                <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
              <label className="field field--row">
                <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                <span>{t('legajo.fieldRequired')}</span>
              </label>
              <label className="field">
                <span>{t('legajo.fieldStatus')}</span>
                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`legajo.status.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('legajo.fieldExpires')}</span>
                <input className="input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('legajo.fieldStorage')}</span>
                <select
                  className="input"
                  value={form.storage_provider}
                  onChange={(e) => setForm({ ...form, storage_provider: e.target.value })}
                >
                  {STORAGE.map((s) => (
                    <option key={s} value={s}>
                      {t(`legajo.storage.${s}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldUrl')}</span>
                <input className="input" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} />
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldFileName')}</span>
                <input className="input" value={form.file_name} onChange={(e) => setForm({ ...form, file_name: e.target.value })} />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(null)}>
                {t('employees.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={modal === 'create' ? saveCreate : saveEdit}
                disabled={!form.employee_id || !form.document_type || !form.title}
              >
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {payslipModal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{t('legajo.newPayslip')}</h2>
            <div className="form-grid">
              <label className="field field--full">
                <span>{t('legajo.fieldEmployee')}</span>
                <select
                  className="input"
                  value={payslipModal.employee_id}
                  onChange={(e) => setPayslipModal({ ...payslipModal, employee_id: e.target.value })}
                >
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.first_name} {e.last_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('legajo.payslipFieldMonth')}</span>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={12}
                  value={payslipModal.period_month}
                  onChange={(e) => setPayslipModal({ ...payslipModal, period_month: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('legajo.payslipFieldYear')}</span>
                <input
                  className="input"
                  type="number"
                  min={2000}
                  max={2100}
                  value={payslipModal.period_year}
                  onChange={(e) => setPayslipModal({ ...payslipModal, period_year: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('legajo.fieldTitle')}</span>
                <input className="input" value={payslipModal.title} onChange={(e) => setPayslipModal({ ...payslipModal, title: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('legajo.payslipFieldGross')}</span>
                <input className="input" value={payslipModal.gross} onChange={(e) => setPayslipModal({ ...payslipModal, gross: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('legajo.payslipFieldNet')}</span>
                <input className="input" value={payslipModal.net} onChange={(e) => setPayslipModal({ ...payslipModal, net: e.target.value })} />
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setPayslipModal(null)}>
                {t('employees.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={savePayslipCreate}
                disabled={!payslipModal.employee_id || !payslipModal.period_month || !payslipModal.period_year}
              >
                {t('employees.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadTarget && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{uploadTarget.kind === 'payslip' ? t('legajo.receiptUploadTitle') : t('legajo.docUploadTitle')}</h2>
            <p className="panel__muted">{storageHint}</p>
            {storageConfig && (
              <p className="panel__muted text-sm">
                {t('legajo.uploadMaxLabel')} {storageConfig.max_upload_mb} MB. {t('legajo.uploadTypesLabel')}{' '}
                {(storageConfig.allowed_mime_types || []).join(', ')}
              </p>
            )}
            <div className="field field--full">
              <input
                type="file"
                className="input"
                disabled={uploadBusy}
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  if (!f) return;
                  if (uploadTarget.kind === 'payslip') uploadPayslipFile(uploadTarget.id, f);
                  else uploadHrFile(uploadTarget.id, f);
                }}
              />
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setUploadTarget(null)} disabled={uploadBusy}>
                {t('legajo.closeMeta')}
              </button>
            </div>
          </div>
        </div>
      )}

      {metaDoc && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal modal--wide">
            <h2 className="modal__title">{t('legajo.metaTitle')}</h2>
            <pre className="legajo-meta-json">{JSON.stringify(metaDoc, null, 2)}</pre>
            <div className="modal__actions">
              <button type="button" className="btn btn--primary" onClick={() => setMetaDoc(null)}>
                {t('legajo.closeMeta')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
